import { Presentation, Slide } from '@features/powerpoint';
import { BuildReportOptions } from './build-presentation';
import { visibleSlide } from '@features/executive-report/slides';
import { titleSlide } from '@features/executive-report/slides/v2/title';
import { agendaSlide } from '@features/executive-report/slides/v2/agenda';
import { coverSlide } from '@features/executive-report/slides/v2/cover';
import { salesCampaignOverviewSlide } from '@features/executive-report/slides/v2/sales-campaign-overview';
import { salesCampaignOverviewSlide2 } from '@features/executive-report/slides/v2/sales-campaign-overview-2';
import { buildNavbarText } from '@features/executive-report/slides/v2/components/navbar-texts';
import { visitOverviewSlide } from '@features/executive-report/slides/v2/visit-overview';
import { buildKeyDriversCards } from '../slides/v2/sales-campaign-overview-2/build-key-drivers-cards';
import { navbarSections } from '../domain/navbar-sections';
import { salesDimensionSlides } from '../slides/v2/sales-dimensions';
import { visitDimensionsSlides } from '../slides/v2/visit-dimensions';
import { howWeDoItSlide } from '@features/executive-report/slides/v2/how-we-do-it';
import { optimizationChannelsSlide } from '@features/executive-report/slides/v2/optimization-channels';
import { campaignInsightsSlides } from '../slides/v2/campaign-insights-slides';

export type BuildReportOptionsV2 = Extract<
  BuildReportOptions,
  { designVersion: 'v2' }
>;

const buildSalesSlides = (options: BuildReportOptionsV2): Slide[] => {
  const hasSalesMetrics = !!options.currentSalesMetrics;

  if (!hasSalesMetrics) return [];

  const keyDriversCards = buildKeyDriversCards({
    penetrationRate: {
      total: options.currentSalesMetrics?.penetrationLift.total,
      exposed: options.currentSalesMetrics?.exposedPenetration.total,
      control: options.currentSalesMetrics?.controlPenetration.total,
    },
    transactionSize: {
      total: options.currentSalesMetrics?.transactionSizeLift.total,
      exposed: options.currentSalesMetrics?.exposedTransactionSize.total,
      control: options.currentSalesMetrics?.controlTransactionSize.total,
    },
    transactionRate: {
      total: options.currentSalesMetrics?.transactionRateLift.total,
      exposed: options.currentSalesMetrics?.exposedTransactionRate.total,
      control: options.currentSalesMetrics?.controlTransactionRate.total,
    },
  });

  return [
    ...visibleSlide({
      slide: coverSlide('Sales Lift'),
      visible: options.customFields.includedSlides.salesLift.cover,
    }),
    ...visibleSlide({
      slide: salesCampaignOverviewSlide({
        navbarText: buildNavbarText(navbarSections, 'Overview'),
        adSpend: options.calculatedFields.adSpend ?? 0,
        flightDates: [options.campaign.startDate, options.campaign.endDate],
        measurementPeriod: [
          options.currentReport.startDate,
          options.currentReport.reportDate,
        ],
        uniqueUsers: options.currentReport.visitMetrics.uniqueUsers,
        impressions: options.currentReport.visitMetrics.impressions,
        sections: [
          {
            name: 'Who',
            count: options.subdimensionCounts.aud ?? 0,
            dimensionName: 'Audience',
          },
          {
            name: 'What',
            count: options.subdimensionCounts.creative ?? 0,
            dimensionName: 'Creative',
          },
          {
            name: 'Where',
            count: options.subdimensionCounts.channel ?? 0,
            dimensionName: 'Channel',
          },
        ],
      }),
      visible: options.customFields.includedSlides.salesLift.campaignOverview,
    }),
    ...visibleSlide({
      slide: salesCampaignOverviewSlide2({
        navbarText: buildNavbarText(navbarSections, 'Overview'),
        incrementalLift:
          options.currentSalesMetrics?.incrementalSalesLift.total,
        incrementalSales: options.currentSalesMetrics?.incrementalSales.total,
        iroas: options.calculatedFields.incrementalReturnOnAdSpend,
        objectives: options.customFields.salesObjectives.map(o => o.value),
        keyDrivers: keyDriversCards,
      }),
      visible: options.customFields.includedSlides.salesLift.campaignOverview2,
    }),
    ...salesDimensionSlides(options),
    ...visibleSlide({
      slide: optimizationChannelsSlide({
        midCampaignSummary: options.midCampaignSalesMetrics
          ? {
              lift: options.midCampaignSalesMetrics.incrementalSales.total ?? 0,
              totalIncrementality:
                options.midCampaignSalesMetrics.incrementalSalesLift.total ?? 0,
              transactionRateSales:
                options.midCampaignSalesMetrics.transactionRateSales.total ?? 0,
              transactionRateLift:
                options.midCampaignSalesMetrics.transactionRateLift.total ?? 0,
              transactionSizeSales:
                options.midCampaignSalesMetrics.transactionSizeSales.total ?? 0,
              transactionSizeLift:
                options.midCampaignSalesMetrics.transactionSizeLift.total ?? 0,
              penetrationSales:
                options.midCampaignSalesMetrics.penetrationSales.total ?? 0,
              penetrationLift:
                options.midCampaignSalesMetrics.penetrationLift.total ?? 0,
            }
          : null,
        endCampaignSummary: {
          lift: options.currentSalesMetrics?.incrementalSales.total ?? 0,
          totalIncrementality:
            options.currentSalesMetrics?.incrementalSalesLift.total ?? 0,
          transactionRateSales:
            options.currentSalesMetrics?.transactionRateSales.total ?? 0,
          transactionRateLift:
            options.currentSalesMetrics?.transactionRateLift.total ?? 0,
          transactionSizeSales:
            options.currentSalesMetrics?.transactionSizeSales.total ?? 0,
          transactionSizeLift:
            options.currentSalesMetrics?.transactionSizeLift.total ?? 0,
          penetrationSales:
            options.currentSalesMetrics?.penetrationSales.total ?? 0,
          penetrationLift:
            options.currentSalesMetrics?.penetrationLift.total ?? 0,
        },
      }),
      visible:
        options.customFields.includedSlides.salesLift.optimizationSummary,
    }),
  ];
};

export const buildSlidesV2 = (
  options: BuildReportOptionsV2
): Presentation['slides'] => [
  ...visibleSlide({
    slide: titleSlide(
      options.campaign,
      options.brandLogo,
      options.customFields.campaignName
    ),
    visible: options.customFields.includedSlides.intro.title,
  }),
  ...visibleSlide({
    slide: agendaSlide([
      { title: 'The Who', text: ' - Audience' },
      { title: 'The What', text: ' - Creative' },
      { title: 'The Where', text: ' - Channels' },
    ]),
    visible: options.customFields.includedSlides.intro.agenda,
  }),
  ...visibleSlide({
    slide: howWeDoItSlide(),
    visible: options.customFields.includedSlides.intro.howWeDoIt,
  }),
  ...visibleSlide({
    slide: coverSlide('Visit Lift'),
    visible: options.customFields.includedSlides.visitLift.cover,
  }),
  ...visibleSlide({
    slide: visitOverviewSlide({
      navbarText: buildNavbarText(navbarSections, 'Overview'),
      brandObjective: options.customFields.footTrafficObjectives
        .map(o => o.value)
        .join(' '),
      cpiv: options.calculatedFields.costPerIncrementalVisit,
      incrementalVisitLift: options.currentReport.visitMetrics.incrementalLift,
      totalIncrementalVisits:
        options.currentReport.visitMetrics.incrementalVisits,
      totalProjectedVisits: options.currentReport.visitMetrics.projectedVisits,
      uniqueUsers: options.currentReport.visitMetrics.uniqueUsers,
      impressions: options.currentReport.visitMetrics.impressions,
    }),
    visible: options.customFields.includedSlides.visitLift.overview,
  }),
  ...visitDimensionsSlides(options),
  ...buildSalesSlides(options),
  ...campaignInsightsSlides(
    options.campaignAnalyticsReport,
    options.customFields.includedSlides.campaignInsights
  ),
];
